import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import i18n from "./i18n";
import axios from "axios";
import Swal from "sweetalert2";
import tooltip from "./directives/tooltip.js";
import "@/assets/tooltip.css";
import VueShepherdPlugin from "vue-shepherd";
import "shepherd.js/dist/css/shepherd.css";
import appInsights from "./services/appInsights.js";
import tabCloseWarningService from "./services/tabCloseWarning.js";
import authService from "./services/auth.service";

const app = createApp(App).use(i18n).use(VueShepherdPlugin);

app.directive("tooltip", tooltip);

app.mixin({
  methods: {
    $trackEvent(obj) {
      appInsights.trackEvent(obj);
    },
    $trackPageView(obj) {
      appInsights.trackPageView(obj);
    },
    $trackException(obj) {
      appInsights.trackException(obj);
    },
    $trackTrace(obj) {
      appInsights.trackTrace(obj);
    },
    $trackMetric(obj) {
      appInsights.trackMetric(obj);
    },
    $trackDependencyData(obj) {
      appInsights.trackDependencyData(obj);
    },
    $startTrackPage(page) {
      appInsights.startTrackPage(page);
    },
    $stopTrackPage(page) {
      appInsights.stopTrackPage(page);
    },
    $startTrackEvent(event) {
      appInsights.startTrackEvent(event);
    },
    $stopTrackEvent(event) {
      appInsights.stopTrackEvent(event);
    },
    $flush() {
      appInsights.flush();
    },
  },
});

app.mixin({
  methods: {
    $success(msg) {
      const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", Swal.stopTimer);
          toast.addEventListener("mouseleave", Swal.resumeTimer);
        },
      });

      Toast.fire({
        icon: "success",
        title: `${msg}`,
      });
    },
    $error(msg, error = null) {
      if (error !== null) {
        if (axios.isCancel(error)) return;
        if (error.message === "error.response is undefined") return;
        console.log(error);
      }
      const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", Swal.stopTimer);
          toast.addEventListener("mouseleave", Swal.resumeTimer);
        },
      });

      Toast.fire({
        icon: "error",
        title: `${msg}`,
      });
    },
    $warning(msg) {
      const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", Swal.stopTimer);
          toast.addEventListener("mouseleave", Swal.resumeTimer);
        },
      });

      Toast.fire({
        icon: "warning",
        title: `${msg}`,
      });
    },
    $info(msg) {
      const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", Swal.stopTimer);
          toast.addEventListener("mouseleave", Swal.resumeTimer);
        },
      });

      Toast.fire({
        icon: "info",
        title: `${msg}`,
      });
    },
    $can(permissionName) {
      if (!this.$store.state.auth?.user?.permissions) return false;
      return this.$store.state.auth?.user?.permissions?.includes(
        permissionName
      );
    },
  },
});

import "viewerjs/dist/viewer.css";
import VueViewer from "v-viewer";

tabCloseWarningService.install();

app
  .use(router)
  .use(store)
  .use(VueViewer)
  .mixin({
    methods: {},
  })
  .mount("#app");

axios.defaults.withCredentials = true;

let cancelSource = axios.CancelToken.source();

const requestInterceptor = (config) => {
  config.cancelToken = cancelSource.token;
  return config;
};

axios.interceptors.request.use(requestInterceptor);

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (!error.response) {
      return Promise.reject(error);
      // console.log("No error response");
      // return error;
    }
    if (error.response.status === 401 && router.currentRoute.name !== "login") {
      if (error.config.url == "https://api.varsanpr.com/api/auth/refresh") {
        // Prevent infinite loop if refresh token request fails
        console.log("Refresh token request failed, redirecting to login");
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        window.location.href =
          "https://gateway.varsanpr.com/login?redirect=" +
          window.location.origin +
          "/login";
        return Promise.reject(error);
      }
      // 401, attempt to refresh token
      if (!authService.refreshPromise) {
        authService.refreshPromise = authService.refreshToken().then(() => {
          authService.refreshPromise = null;
        });
      }
      await authService.refreshPromise;
      return axios(error.config);
    }

    if (error.response.status === 403) {
      if (error.response.data !== undefined) {
        if (error.response.data["2fa"] === "required") {
          console.info("2FA IS REQUIRED");
          router.replace({
            name: "2fa",
            params: {
              route: router.currentRoute.name,
              params: router.currentRoute.params,
              first: true,
            },
          });
          return error;
        }
        if (error.response.data["2fa"] === "expired") {
          console.info("2FA IS REQUIRED");
          router.replace({
            name: "twoFactorAuth",
            params: {
              route: window.location.pathname + window.location.search,
              first: false,
            },
          });
          return error;
        }
      }
      console.log("Error Response is 403");
      router.replace("/");
    }

    if (axios.isCancel(error)) {
      console.log("Error is due to cancellation by user.");
    }
    return Promise.reject(error);
    // return error;
  }
);
